import { z } from "zod";
import { RARETES, TYPES_EDITION } from "./carte-impression";

/**
 * Le contrat de l'accueil public de La Taverne : `GET /api/taverne/etat`, sans session. Les deux cotes le lisent par
 * leur pont `src/app/contract.ts`.
 *
 * Rien d'une personne n'y passe : ni viewer, ni compteur. Seulement le stream de la chaine, public sur Twitch.
 */

/**
 * L'etat du stream. `enDirect` vaut vrai quand un stream est ouvert ; `titre` et `jeu` sont ceux que la base
 * connait (ils peuvent manquer, meme en direct) ; `depuis` est l'heure d'ouverture du stream, en ISO 8601.
 * Hors ligne, les trois valent `null`.
 */
export const etatTaverneSchema = z.object({
  enDirect: z.boolean(),
  titre: z.string().nullable(),
  jeu: z.string().nullable(),
  depuis: z.string().nullable(),
});
export type EtatTaverne = z.infer<typeof etatTaverneSchema>;

/**
 * La page publique d'une carte (lot 5 du TCG, PR 5) : `GET /api/taverne/cartes/{ref}`, sans session. Une carte n'est
 * publique qu'une fois imprimee ET validee (une carte de raid, des qu'elle est imprimee) ; une carte inconnue,
 * retiree, d'une personne en opposition, en attente ou remplacee rend la meme 404.
 *
 * C'est la seule route publique qui nomme une personne, et le nom y est le sien : celui de sa carte, que la
 * politique de confidentialite dit visible de tous. Aucun identifiant (ni Twitch, ni de base) n'en sort.
 */

/** `<numero d'album>-<numero d'edition>` : « 7-3 » est la 3e edition de la carte n° 7 de la saison ouverte. */
export const refCarteSchema = z.string().regex(/^(0|[1-9][0-9]{0,3})-([1-9][0-9]{0,2})$/);

export const refCarteParamsSchema = z.object({ ref: refCarteSchema });

/** `?v=N` de l'image : sa version. Ignoree par le serveur (il sert toujours l'image courante), lue pour le cache. */
export const imageCarteQuerySchema = z.object({ v: z.string().max(12).optional() });

export const cartePubliqueSchema = z.object({
  ref: refCarteSchema,
  nom: z.string(),
  rarete: z.enum(RARETES),
  type: z.enum(TYPES_EDITION),
  /** « 1re éd. », « 3e éd. », « hors série ». */
  edition: z.string(),
  saison: z.number().int(),
  numeroAlbum: z.number().int(),
  /** `null` : « Chronique en cours d'ecriture » (une carte de raid pas encore validee). */
  chronique: z.string().nullable(),
  mentionIa: z.boolean(),
  stats: z.array(z.object({ libelle: z.string(), valeur: z.number().int() })),
  /** Combien de personnes ont au moins un exemplaire de cette edition. */
  possesseurs: z.number().int(),
  /** Combien d'exemplaires sont sortis. */
  exemplaires: z.number().int(),
  tirageMax: z.number().int().nullable(),
  /** « /api/taverne/cartes/7-3/image?v=2 » : la version change a chaque impression. */
  image: z.string(),
  /** Vrai pour la derniere edition validee de la carte. */
  enCirculation: z.boolean(),
});
export type CartePublique = z.infer<typeof cartePubliqueSchema>;

/** Les trois motifs de la fenetre de signalement. */
export const MOTIFS_SIGNALEMENT = ["blessante", "privee", "autre"] as const;
export type MotifSignalement = (typeof MOTIFS_SIGNALEMENT)[number];

/** `POST /api/taverne/cartes/{ref}/signalement` : le motif seul. Rien sur le signaleur ne voyage ni ne se garde. */
export const signalementCorpsSchema = z.object({ motif: z.enum(MOTIFS_SIGNALEMENT) });
export type SignalementCorps = z.infer<typeof signalementCorpsSchema>;

/** Combien de signalements la base accepte par heure, toutes cartes confondues (decision 16). */
export const SIGNALEMENTS_PAR_HEURE = 30;

/**
 * Le viewer connecte (ticket 3 du TCG) : `GET /api/moi`, par la session d'un viewer. Elle ne s'ouvre ni a un anonyme, ni
 * a la session de la regie : `/api/moi` dit « qui es-tu pour La Taverne », et Nicolas n'y est personne. Ne pas la
 * confondre avec `/api/me` du template, qui est « Mes donnees » (export et effacement, pour n'importe quelle session).
 *
 * Seulement ce que le site montre dans son en-tete et sa pastille : le nom affiche, la date d'entree, les pochettes
 * scellees, l'etat du direct et l'image. Aucun identifiant (ni Twitch, ni de base), aucune adresse, aucun jeton.
 */
export const moiSchema = z.object({
  /** Le nom affiche, celui que Twitch a donne. */
  nom: z.string(),
  /** Quand la personne a rejoint, en ISO 8601. */
  rejointLe: z.string(),
  /** Les pochettes qui l'attendent : la pastille « 4 boosters t'attendent ». */
  boostersScelles: z.number().int().nonnegative(),
  /** Vrai quand un stream est ouvert : la meme valeur que `GET /api/taverne/etat`. */
  enDirect: z.boolean(),
  /** Une image du CDN de Twitch (`https://static-cdn.jtvnw.net/...`), ou `null` : le site dessine alors un monogramme. */
  avatarUrl: z.string().nullable(),
});
export type Moi = z.infer<typeof moiSchema>;

/**
 * Les refus de la porte `/api/moi`, une liste fermee : le site en tire sa phrase, jamais le message du serveur. `unauthorized`
 * ne dit pas pourquoi (pas de session, session de la regie, accord retire, opposition, bot : le meme refus), `origin_refused`
 * est une ecriture qui ne vient pas du site, `base_indisponible` une base qui n'a pas repondu. `flux_plein` est le flux
 * `GET /api/moi/flux` quand les 300 flux de viewers du processus sont ouverts, `arret_en_cours` le meme flux pendant l'arret
 * de l'app : dans les deux cas le site relit son etat de temps en temps, sans flux. Les autres codes d'une reponse
 * (`not_found` pour un telephone qui n'est pas a la personne ou une adresse inconnue, `internal_error`) sont ceux du template.
 */
export const CODES_REFUS_MOI = [
  "unauthorized",
  "origin_refused",
  "base_indisponible",
  "flux_plein",
  "arret_en_cours",
] as const;
export type CodeRefusMoi = (typeof CODES_REFUS_MOI)[number];

export const refusMoiSchema = z.object({
  error: z.object({
    code: z.enum(CODES_REFUS_MOI),
    message: z.string(),
    requestId: z.string().optional(),
  }),
});
export type RefusMoi = z.infer<typeof refusMoiSchema>;

/**
 * Les telephones relies (ticket 3 du TCG, PR 5) : `GET /api/moi/telephones` rend une ligne par session ouverte du compte,
 * `DELETE /api/moi/telephones/{id}` en delie une. Une session est un telephone ou un navigateur ; elle garde l'adresse IP et
 * le navigateur de la connexion (septieme traitement de la politique de confidentialite), **mais ni l'une ni l'autre ne sort
 * d'ici** : la personne reconnait son appareil par un libelle d'une liste fermee et par sa date, rien de plus. Aucun jeton non
 * plus : `id` est l'identifiant de la ligne de session, qui n'ouvre rien.
 */
export const LIBELLES_TELEPHONE = ["iPhone", "Android", "Ordinateur", "Autre appareil"] as const;
export type LibelleTelephone = (typeof LIBELLES_TELEPHONE)[number];

export const telephoneSchema = z.object({
  /** L'identifiant de la session : opaque, a renvoyer tel quel a `DELETE /api/moi/telephones/{id}`. Jamais le jeton. */
  id: z.string(),
  /** Tire du navigateur par une liste fermee : « iPhone », « Android », « Ordinateur » ou « Autre appareil ». */
  libelle: z.enum(LIBELLES_TELEPHONE),
  /** Quand cet appareil a ete relie, en ISO 8601 (UTC). */
  relieLe: z.string(),
  /** Vrai pour la session qui fait cette demande : le site ecrit « cet appareil ». Un seul telephone de la liste. */
  cetAppareil: z.boolean(),
});
export type Telephone = z.infer<typeof telephoneSchema>;

export const telephonesSchema = z.object({ telephones: z.array(telephoneSchema) });
export type Telephones = z.infer<typeof telephonesSchema>;

export const telephoneParamsSchema = z.object({ id: z.string().min(1).max(128) });

/**
 * Un message du flux d'un viewer (`GET /api/moi/flux`, ticket 3 du TCG, PR 5), des mots fermes. Ce flux n'est pas le bus du
 * direct (`messageDirectSchema`) : il porte ce qui concerne UNE personne, et un viewer ne recoit jamais le message d'un autre.
 * - `etat` : l'etat du direct, envoye a l'ouverture de chaque flux (ce que `GET /api/moi` dit de `enDirect`) ;
 * - `direct` : le stream vient de s'ouvrir ou de se fermer (`enDirect` est la nouvelle valeur) ;
 * - `delie` : cette session n'est plus reliee (telephone delie, deconnexion, ou la personne a quitte le chateau, s'est opposee
 *   ou a ete effacee). Le flux se termine juste apres ; le site ramene sur `/rejoindre`.
 * Les tickets suivants y ajoutent leurs mots (`boosters`, ticket 6).
 */
export const messageFluxViewerSchema = z.discriminatedUnion("genre", [
  z.object({ genre: z.literal("etat"), enDirect: z.boolean() }),
  z.object({ genre: z.literal("direct"), enDirect: z.boolean() }),
  z.object({ genre: z.literal("delie") }),
]);
export type MessageFluxViewer = z.infer<typeof messageFluxViewerSchema>;
